import { queryOptions, useQuery } from "@tanstack/react-query";
import { API_HOST } from "../../App";
import { z } from "zod";
import { useFlags } from "../useFlags";

const addonItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  min: z.number(),
  max: z.number(),
  externalId: z.string(),
  priority: z.number(),
  price: z.number(),
});

const addonGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  min: z.number(),
  max: z.number(),
  items: z.array(addonItemSchema),
  requiredProductOptionIds: z.array(z.string()).optional(),
});

const condimentSchema = z.object({
  groupId: z.number(),
  name: z.string(),
  rule: z
    .object({
      condimentGroupRef: z.number(),
      minimumCount: z.number(),
      maximumCount: z.number(),
    })
    .nullable(),
  condiments: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      price: z.number(),
    })
  ),
});

const optionSchema = z.object({
  id: z.string(),
  name: z.string(),
  externalId: z.string(),
  description: z.string().nullable().optional(),
  quantity: z.number(),
  default: z.boolean(),
  type: z.string(),
  hidden: z.boolean(),
  group: z.string().nullable(),
  image: z.string().nullable(),
  kitchenNote: z.string().nullable(),
  price: z.number(),
  priority: z.number(),
  popular: z.boolean(),
  productId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  //addons: z.array(addonItemSchema).optional(),
  condiments: z.array(condimentSchema).optional(),
});

const productSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  categoryId: z.string(),
  image: z.string(),
  unavailable: z.boolean().nullish().optional(),
  paused_until: z.string().nullish().optional(),
  can_purchase: z.boolean().nullish().optional().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
  options: z.array(optionSchema),
  addonGroups: z.array(addonGroupSchema),
  priority: z.number(),
});

const productWIthoutAddonsSchema = productSchema
  .setKey("addonGroups", z.array(addonGroupSchema).optional())
  .setKey("priority", z.number().optional());

const categorySchema = z.object({
  id: z.string(),
  name: z.string(),
  locationId: z.string(),
  priority: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  defaultOnPremiseDeliveryMethod: z
    .enum(["TABLE_SERVICE", "PICKUP_AT_COUNTER"])
    .optional()
    .nullable(),
  products: z.array(productSchema),
});

const categoryWithoutAddonsSchema = categorySchema.setKey(
  "products",
  z.array(productWIthoutAddonsSchema)
);

const menuSchema = z.object({
  id: z.string(),
  image: z.string().nullable(),
  description: z.string().nullable(),
  name: z.string(),
  priority: z.number(),
  locationId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  categories: z.array(categorySchema),
  netsEnvironment: z.string(),
  netsCheckoutKey: z.string(),
  locationName: z.string(),
  locationCvr: z.string(),
  isSimphony: z.boolean().optional().default(false),
  type: z.enum(["DINE_IN", "TAKE_AWAY", "DELIVERY"]),
  visibleHours: z
    .array(
      z.object({
        startDay: z.enum([
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ]),
        closeDay: z.enum([
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ]),
        startHour: z.string(),
        closeHour: z.string(),
      })
    )
    .nullable(),
});

const menuWithoutAddonsSchema = menuSchema.setKey(
  "categories",
  z.array(categoryWithoutAddonsSchema)
);

export type MenuAddonGroup = z.infer<typeof addonGroupSchema>;
export type MenuAddonItem = z.infer<typeof addonItemSchema>;
export type MenuOption = z.infer<typeof optionSchema>;
export type MenuItem = z.infer<typeof productSchema>;
export type MenuCategory = z.infer<typeof categorySchema>;

export type RestaurantMenu = z.infer<typeof menuSchema>;

const MenuResponse = menuSchema;
const MenuWithoutAddonsResponse = menuWithoutAddonsSchema;

export class ClosedError extends Error {
  constructor(message: string | undefined) {
    super(message);
    this.name = "ClosedError";
  }
}

export const menuQueryOptions = (menuId: string) =>
  queryOptions({
    queryKey: ["menu", menuId],
    queryFn: async () => {
      throw new Error(
        "Depreacted API call used - use allMenusQueryOptions instead"
      );
    },
  });

export const allMenusQueryOptions = (locationId: string) =>
  queryOptions({
    queryKey: ["menus", locationId],
    queryFn: async () => {
      const response = await fetch(
        `${API_HOST}/v1/menus/${locationId}/availableMenus`
      );
      const data = await response.json();
      if (response.status == 423) {
        throw new ClosedError(data?.message ?? "");
      }

      // Add "<script src="https://checkout.dibspayment.eu/v1/checkout.js?v=1"></script>" to index.html if it doesn't exist

      const menus = data as unknown[];
      const { addonsAvailable } = useFlags();

      try {
        const parsed = (
          addonsAvailable
            ? menus?.map((menu) => MenuResponse.parse(menu))
            : menus?.map((menu) => MenuWithoutAddonsResponse.parse(menu))
        ) as RestaurantMenu[];

        const anyMenuIsTest = parsed.some(
          (menu) => menu.netsEnvironment === "TEST"
        );

        const netsCheckoutScript = anyMenuIsTest
          ? "https://test.checkout.dibspayment.eu/v1/checkout.js?v=1"
          : "https://checkout.dibspayment.eu/v1/checkout.js?v=1";

        if (!window.document.getElementById("nets-checkout-script")) {
          window.document.body.appendChild(
            Object.assign(document.createElement("script"), {
              src: netsCheckoutScript,
              id: "nets-checkout-script",
            })
          );
        }

        return parsed;
      } catch (error) {
        console.error(error);
        if (error instanceof z.ZodError) {
          console.log(error.issues);
        }
        throw new Error("Error when parsing menu - please contact support");
      }
    },
  });

export const useMenu = (menuId: string) => {
  return useQuery(menuQueryOptions(menuId));
};
